import React, {
  useCallback,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import translate from '../../../commonComponents/translate';
import NetworkListTable from './NetworkListTable';
import NetworkListDateInfo from './NetworkListDateInfo';
import NetworkListFilters from './NetworkListFilters';
import ConfigsContext from '../../../contextProviders/ConfigsContext';
import { getInitialState } from '../helpers/auxiliarTreeHelper';
import Notifications from '../../../services/notifications';
import Loader from '../../../services/loader';
import { compose } from '../../../services/utilityFunctions';
import withCSRF from '../../../commonComponents/withCSRF';

const defaultFilters = {
  search: null,
  sponsoredBy: null,
  recruitedBy: null,
  level: null,
  showInvited: false,
  user: null,
};

function NetworkList({ t, _csrf }) {
  const loader = useRef(new Loader('#progressLoader'));
  const [isLoading, setLoading] = useState(false);
  const pollingTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  const { organization = {} } = useContext(ConfigsContext);
  const { recurrence } = organization?.directSelling || {};
  const initialState = getInitialState(recurrence);
  const [filters, setFilters] = useState({
    ...defaultFilters,
    startDate: initialState.startDate,
    endDate: initialState.endDate,
    datePicker: initialState.rangeType,
  });

  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    },
    [],
  );

  const handleFilters = useCallback(
    field => value => {
      switch (field) {
        case 'showInvited':
          setFilters(prevFilters => ({
            ...prevFilters,
            showInvited: !prevFilters.showInvited,
          }));
          break;
        default:
          setFilters(prevFilters => ({ ...prevFilters, [field]: value }));
          break;
      }
    },
    [setFilters],
  );
  /** endregion */

  const handleClearFilters = useCallback(
    () => setFilters(initialState),
    [setFilters],
  );

  const handleDateChange = useCallback(
    (startDate, endDate, rangeType) => {
      setFilters(prevState => ({
        ...prevState,
        startDate,
        endDate,
        datePicker: rangeType,
      }));
    },
    [setFilters],
  );
  // Export list excel (now async job-based)
  const handleExportList = useCallback(() => {
    const params = { ...filters };
    // Remove null/empty params
    const cleanParams = Object.fromEntries(
      Object.entries({
        ...params,
        populate: ['sponsoredBy', 'recruitedBy'],
        showInvited: filters.showInvited ? filters.showInvited : null,
        search:
          filters.search && filters.search.length > 0 ? filters.search : null,
      }).filter(([, v]) => v !== null && v !== undefined),
    );
    setLoading(true);
    axios
      .post('/jobs/user/network/list/xlsx', cleanParams, {
        headers: { 'X-CSRF-Token': _csrf },
      })
      .then(response => {
        const jobId = response?.data?.data?.id;
        // Poll job status
        const pollInterval = 1000;
        const checkJobStatus = (retryCount = 0, maxRetries = 3) => {
          if (!isMountedRef.current) {
            console.log('Component unmounted, stopping poll');
            return;
          }
          axios
            .get(`/jobs/user/importStatus/${jobId}`, { timeout: 10000 })
            .then(res => {
              if (!isMountedRef.current) {
                return;
              }

              const { progress, finishedOn, failedReason } =
                res.data.data || res.data;

              if (loader.current) {
                loader.current.showProgressLoader(
                  `${progress?.toFixed(0) || 0}% ${t(
                    'order_status_processing',
                  )}...`,
                );
              }

              if (finishedOn) {
                setLoading(false);
                if (pollingTimeoutRef.current) {
                  clearTimeout(pollingTimeoutRef.current);
                  pollingTimeoutRef.current = null;
                }
                if (failedReason) {
                  Notifications.showNotificationError(
                    'Export failed',
                    failedReason,
                  );
                } else {
                  Notifications.showNotificationSuccess(
                    'Export',
                    'Export completed. Downloading...',
                  );
                  const link = document.createElement('a');
                  link.href = `/excel-jobs/user/network/list/job/${jobId}/download`;
                  link.download = '';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }
              } else {
                // Schedule next poll
                pollingTimeoutRef.current = setTimeout(
                  () => checkJobStatus(),
                  pollInterval,
                );
              }
            })
            .catch(() => {
              if (!isMountedRef.current) {
                return;
              }

              // Retry on network errors
              if (retryCount < maxRetries) {
                const backoffDelay = pollInterval * 2 ** retryCount;
                console.log(
                  `Retrying in ${backoffDelay}ms (attempt ${
                    retryCount + 1
                  }/${maxRetries})`,
                );
                pollingTimeoutRef.current = setTimeout(
                  () => checkJobStatus(retryCount + 1, maxRetries),
                  backoffDelay,
                );
                return;
              }

              setLoading(false);
              if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
                pollingTimeoutRef.current = null;
              }
              Notifications.showNotificationError(
                'Export',
                'Failed to check export job status. The job may still be processing.',
              );
            });
        };
        checkJobStatus();
      })
      .catch(() => {
        setLoading(false);
        Notifications.showNotificationError(
          'Export',
          'Failed to start export job.',
        );
      });
  }, [filters, _csrf, t]);

  return (
    <div className="m-portlet__body" id="networkListTab">
      {isLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div id="progressLoader" />
        </div>
      )}
      <NetworkListDateInfo />
      <NetworkListFilters
        filters={filters}
        onFiltersChange={handleFilters}
        onExportList={handleExportList}
        onClearFilters={handleClearFilters}
        onDateChange={handleDateChange}
      />
      <NetworkListTable filters={filters} />
    </div>
  );
}

NetworkList.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
};

export default compose(translate, withCSRF)(NetworkList);
